import { jsx as _jsx } from "react/jsx-runtime";
// Assistant-bubble markdown renderer (fn-49.2, R2/R3; fn-95 streaming).
//
// ONE renderer for BOTH the in-flight token preview and the durable
// message. Streamdown splits the source into blocks and completes
// half-open constructs (`remend`), so a table streams as a table
// instead of raw pipes; because preview and durable go through the
// same component with the same config, the durable swap changes only
// the CONTENT — no restyle, no geometry jump.
//
// Safety posture (documented in the package README) — Streamdown's
// DEFAULT rehype chain is `rehype-raw` + `rehype-sanitize` +
// `rehype-harden`, which makes XSS safety sanitizer-config-dependent.
// We do not use it:
//
//   - `rehypePlugins={[]}` REPLACES that default chain (verified
//     against the shipped bundle: `rehypePlugins || <defaults>`).
//     With no `rehype-raw`, raw HTML never becomes an element — it
//     stays a hast `raw` node and renders as escaped text. Same
//     structural immunity react-markdown gave us: no
//     `dangerouslySetInnerHTML`, nothing to misconfigure.
//   - `urlTransform` is react-markdown's `defaultUrlTransform` —
//     passed EXPLICITLY and never replaced with an identity transform
//     (that exact mistake is GHSA-fpw4-p57j-hqmq). It empties every
//     href/src whose protocol is not `https?|ircs?|mailto|xmpp`:
//     `javascript:`, `vbscript:`, `file:` and ALL `data:` URIs come
//     out as `""`.
//     **Streamdown's OWN `defaultUrlTransform` is the identity
//     function** (it delegates URL safety to `rehype-harden`, which
//     we drop) — importing that one instead re-opens the `javascript:`
//     vector. Keep this import pointed at react-markdown, which stays
//     a dependency SOLELY as the single audited source of this
//     function (repo rule: never vendor a copy of security-critical
//     code).
//   - The `components.a` override adds `rel="noopener noreferrer"` +
//     `target="_blank"`. `urlTransform` runs BEFORE the components map
//     (it rewrites hast properties; the map runs at
//     hast→JSX), so the override only ever receives sanitized hrefs.
//   - Images are NOT rendered. `defaultUrlTransform` still allows
//     http(s) srcs, and an `<img>` from untrusted assistant text would
//     fire a remote fetch on render (tracking/referrer leak). The
//     `components.img` override degrades image syntax to its alt text;
//     no network request ever leaves the bubble.
//   - No `plugins` prop ⇒ no Shiki, no KaTeX, and **no Mermaid**:
//     mermaid is a lazy import behind the diagram plugin, so the
//     dependency never reaches the client bundle.
//   - Oversize messages (> MAX_MARKDOWN_CHARS) skip the parse entirely
//     and render as plain text — no upstream DoS guard exists.
//   - A per-bubble error boundary catches any parse/render throw and
//     falls back to the same plain-text rendering; one broken message
//     never takes down the chat.
//
// Styling: neither this package nor the console uses Tailwind, so
// Streamdown's built-in utility classes are inert. Output is styled by
// our own stylesheet, which keys off the semantic tags plus the
// `sbui-md` class this component always stamps on the wrapper.
import { Component, memo } from 'react';
import { defaultUrlTransform } from 'react-markdown';
import { Streamdown } from 'streamdown';
/**
 * Messages longer than this render as plain text without parsing
 * (~50KB — far beyond any legitimate chat reply).
 */
export const MAX_MARKDOWN_CHARS = 50_000;
/** Plain-text fallback (oversize / parse failure). `pre-wrap` via CSS. */
function PlainText(props) {
    return _jsx("p", { className: "sbui-md-plain", children: props.content });
}
// Module-level STABLE refs — a fresh object per render would defeat
// Streamdown's per-block memoization (the whole point during streaming).
const COMPONENTS = {
    // Streamdown's default `strong` is a Tailwind <span class="font-semibold">.
    // Restore the real tag — semantics and our CSS both want <strong>.
    strong: 'strong',
    a: ({ node: _node, children, ...rest }) => (
    // Sanitized href (urlTransform already ran). rel+target: correct
    // escape from a future iframe embed (ADR 0027 v2a).
    _jsx("a", { ...rest, rel: "noopener noreferrer", target: "_blank", children: children })),
    // No images: an http(s) <img> src would fetch a remote resource the
    // moment the bubble renders — a tracking pixel from untrusted
    // assistant text. Degrade to the alt text (or nothing).
    img: ({ alt }) => typeof alt === 'string' && alt.length > 0 ? (_jsx("span", { className: "sbui-md-img-alt", children: alt })) : null,
};
/** Empty ⇒ Streamdown's raw/sanitize/harden chain never runs. See header. */
const REHYPE_PLUGINS = [];
/** Per-bubble error boundary: any markdown render throw → plain text. */
class MarkdownErrorBoundary extends Component {
    state = { failed: false };
    static getDerivedStateFromError() {
        return { failed: true };
    }
    // A failure is scoped to the content that caused it. Streaming feeds this
    // boundary a new partial every frame, so latching would let one transient
    // parse error hold the rest of the message — and its durable replacement —
    // in plain text.
    componentDidUpdate(previous) {
        if (this.state.failed && previous.content !== this.props.content) {
            this.setState({ failed: false });
        }
    }
    render() {
        if (this.state.failed)
            return _jsx(PlainText, { content: this.props.content });
        return this.props.children;
    }
}
function MarkdownContentImpl(props) {
    const content = props.content;
    if (content.length > MAX_MARKDOWN_CHARS) {
        return _jsx(PlainText, { content: content });
    }
    return (_jsx(MarkdownErrorBoundary, { content: content, children: _jsx(Streamdown, { className: "sbui-md", components: props.unstable_componentsForTest ?? COMPONENTS, rehypePlugins: REHYPE_PLUGINS, urlTransform: defaultUrlTransform, 
            // No copy/download/fullscreen affordances: they are Tailwind-styled
            // and would render as unstyled naked buttons here.
            controls: false, children: content }) }));
}
/**
 * Markdown renderer for ASSISTANT content only — user input is not
 * markdown-authored and stays literal plain text (bubbles.tsx).
 *
 * Used for the streaming preview AND the durable message; passing
 * partial markdown is expected and safe.
 */
export const MarkdownContent = memo(MarkdownContentImpl);
//# sourceMappingURL=markdown-content.js.map